import React, { useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LeaderboardTable from './LeaderboardTable';
import LinearProgress from '@mui/material/LinearProgress';

import {useData} from '../../../contexts/DataContext';
import {useAuth} from '../../../contexts/AuthContext';
import { normalizePicks } from '../../../lib/normalizePicks';
import { getLogoUrl } from '../../../utils/getLogoUrl';
import { getDisplayName } from '../../../utils/getDisplayName';

const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '5vh',
      marginRight: '5vh',
      minHeight: '70vh',
      textAlign: 'center',
      marginTop: '128px'
    },
    leaderboard: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      margin: 'auto',
      maxWidth: '80vw'
    },
    picks: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      margin: 'auto',
    },
    gameCard: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '150px'
    },
    games: {
      display: 'flex',
      flexWrap: 'wrap'
    },
    header: {
      display: 'flex',
      fontSize: '0.9rem',
      fontWeight: '700',
      alignItems: 'center',
      height: '40px',
      margin: '2vh'
    },
    row: {
      display: 'flex',
      justifyContent: 'center',
      height: '30px',
      textAlign: 'center',
      fontSize:'1.0em',
      borderBottom: '1px solid #F6F6F6',
      padding: '10px'
    },
    cell: {
      padding: '10px'
    },
    icon: {
      maxHeight: '40px',
      maxWidth: '40px',
      width: '40px',
      height: '40px',
      objectFit: 'contain'
    },
    score: {
      width: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    winningPick: {
      backgroundColor: '#90EE90',
    }
  }

// Mobile card-per-game styles
const mobileStyles = {
  gameCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '12px',
    overflow: 'hidden',
  },
  gameHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
  },
  gameLogo: {
    width: '36px',
    height: '36px',
    objectFit: 'contain',
  },
  vsText: {
    fontWeight: '700',
    fontSize: '14px',
    color: '#555',
  },
  pickRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '13px',
  },
  pickRowWinner: {
    backgroundColor: '#e8f5e9',
  },
  pickName: {
    color: '#444',
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '45%',
  },
  pickValue: {
    fontWeight: '600',
    color: '#333',
    textAlign: 'right',
  },
  lockedText: {
    color: '#aaa',
    fontStyle: 'italic',
    textAlign: 'right',
  },
};

const Leaderboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { getGamesForWeek,
        getAllPicksForWeek,
        getCurrentWeek,
        winningTeams,
        userDisplayNames,
        currentSeason,
        } = useData();
    const { isAdmin } = useAuth();
    const [page, setPage] = useState(1);
    const [numberOfWeeks, setNumberOfWeeks] = React.useState(1);
    const [games, setGames] = useState([]);
    const [allPicksForWeek, setAllPicksForWeek] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedPick, setExpandedPick] = useState(null); // tracks which mobile pick-score row is expanded
    const [fetchError, setFetchError] = useState('');
    const season = currentSeason;

    // Picks are keyed by game doc id; normalize once per user (handles legacy
    // array-shaped picks too) so each cell can look up by game.id.
    const picksByUser = React.useMemo(() => {
        const map = {};
        (allPicksForWeek || []).forEach((user) => {
            map[user.name] = normalizePicks(user.picks, games);
        });
        return map;
    }, [allPicksForWeek, games]);

    useEffect(() => {
        if (!season) return; // Wait for real season to load
        let cancelled = false;
        const fetchData = async () => {
            setIsLoading(true);
            setFetchError('');
            try {
                const currentWeek = await getCurrentWeek(season);
                if (cancelled) return;
                setPage(currentWeek);
                setNumberOfWeeks(currentWeek);

                const [games, picks] = await Promise.all([
                    getGamesForWeek(season, currentWeek),
                    getAllPicksForWeek(season, currentWeek)
                ]);
                if (cancelled) return;

                setGames(games);
                setAllPicksForWeek(picks);
            } catch (error) {
                console.error('Error loading leaderboard:', error);
                if (!cancelled) setFetchError('Failed to load data. Please try refreshing.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; };
    }, [season, getCurrentWeek, getGamesForWeek, getAllPicksForWeek]);

    const turnPageRef = React.useRef(0);
    const turnPage = async (event, value) => {
        const weekNum = typeof value === 'object' ? value.target.value : value;
        const requestId = ++turnPageRef.current;
        setPage(weekNum);
        setIsLoading(true);

        try {
            const [games, picks] = await Promise.all([
                getGamesForWeek(season, weekNum),
                getAllPicksForWeek(season, weekNum)
            ]);
            if (requestId !== turnPageRef.current) return;
            setGames(games);
            setAllPicksForWeek(picks);
        } catch (error) {
            console.error('Error loading week:', error);
            if (requestId === turnPageRef.current) {
                setFetchError('Failed to load week. Please try again.');
            }
        } finally {
            if (requestId === turnPageRef.current) setIsLoading(false);
        }
    };

    // Mobile: card-per-game picks
    const renderMobilePicks = () => (
      <div style={{ width: '100%', padding: '0 4px' }}>
        {games && games.map((game) => {
          const gameId = `${game.awayTeam?.name || 'away'}-${game.homeTeam?.name || 'home'}`;
          return (
            <div style={mobileStyles.gameCard} key={gameId}>
              <div style={mobileStyles.gameHeader}>
                {game.awayTeam.logo && (
                  <img style={mobileStyles.gameLogo} src={getLogoUrl(game.awayTeam.logo)} alt={game.awayTeam.name} />
                )}
                <span style={mobileStyles.vsText}>{game.neutralSite ? 'VS' : '@'}</span>
                {game.homeTeam.logo && (
                  <img style={mobileStyles.gameLogo} src={getLogoUrl(game.homeTeam.logo)} alt={game.homeTeam.name} />
                )}
              </div>
              {game.disabled && game.homeScore !== undefined && game.awayScore !== undefined && (
                <div style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#666',
                  fontWeight: '600',
                  padding: '4px 0',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#fafafa',
                }}>
                  Final: {game.awayScore} - {game.homeScore}
                </div>
              )}
              {allPicksForWeek && allPicksForWeek.map((user) => {
                const pick = picksByUser[user.name]?.[game.id];
                if (!pick) {
                  return (
                    <div style={mobileStyles.pickRow} key={user.name}>
                      <span style={mobileStyles.pickName}>{getDisplayName(userDisplayNames.get(user.name), user.name)}</span>
                      <span style={mobileStyles.lockedText}>—</span>
                    </div>
                  );
                }

                // Derive winner from game scores — works for all past games without needing to run scoring
                const winningTeam = (game.homeScore !== undefined && game.awayScore !== undefined)
                  ? (Number(game.homeScore) > Number(game.awayScore) ? game.homeTeam.name : game.awayTeam.name)
                  : (winningTeams && winningTeams[gameId]);
                const pickedWinner = pick.homeWin ? game.homeTeam.name : game.awayTeam.name;
                const isWinner = winningTeam === pickedWinner;
                const showPicks = game.disabled || isAdmin;

                if (!showPicks) {
                  return (
                    <div style={mobileStyles.pickRow} key={user.name}>
                      <span style={mobileStyles.pickName}>{getDisplayName(userDisplayNames.get(user.name), user.name)}</span>
                      <span style={mobileStyles.lockedText}>Locked</span>
                    </div>
                  );
                }

                // Highlight row for pick-score winners only (not pick-winner games)
                const rowStyle = game.isPickScore && pick.isWinningPick
                  ? { ...mobileStyles.pickRow, ...mobileStyles.pickRowWinner }
                  : mobileStyles.pickRow;

                const pickedTeam = pick.awayWin ? game.awayTeam : game.homeTeam;
                const pickedShortName = pickedTeam.shortName || pickedTeam.name;

                const pickKey = `${gameId}-${user.name}`;
                const isExpanded = expandedPick === pickKey;

                return (
                  <div key={user.name}>
                    <div
                      style={{
                        ...rowStyle,
                        ...(game.isPickScore && pick.scoreDifference !== undefined ? { cursor: 'pointer' } : {}),
                      }}
                      onClick={() => {
                        if (game.isPickScore && pick.scoreDifference !== undefined) {
                          setExpandedPick(isExpanded ? null : pickKey);
                        }
                      }}
                    >
                      <span style={mobileStyles.pickName}>{getDisplayName(userDisplayNames.get(user.name), user.name)}</span>
                      <span style={mobileStyles.pickValue}>
                        {game.isPickScore ? (
                          <>
                            {pick.awayScore} - {pick.homeScore}
                            {pick.isWinningPick && pick.isExactMatch && ' ★'}
                          </>
                        ) : (
                          <>
                            {pickedShortName}
                            {winningTeam && (isWinner
                              ? <span style={{ color: '#2e7d32', marginLeft: '6px' }}> ✓</span>
                              : <span style={{ color: '#c62828', marginLeft: '6px' }}> ✗</span>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                    {isExpanded && (
                      <div style={{
                        padding: '4px 12px 8px',
                        fontSize: '11px',
                        color: pick.isExactMatch ? '#2e7d32' : '#666',
                        fontWeight: pick.isExactMatch ? '700' : '400',
                        borderBottom: '1px solid #f0f0f0',
                        textAlign: 'right',
                      }}>
                        {pick.isExactMatch ? 'Exact Score!' : `${pick.scoreDifference} points away`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );

    // Desktop: original horizontal grid (unchanged)
    const renderDesktopPicks = () => (
      <section style={styles.games}>
        <section style={styles.gameCard}>
          <div style={styles.header}></div>
          {allPicksForWeek && allPicksForWeek.map((user) => {
            return <div style={styles.row} key={user.name}>{getDisplayName(userDisplayNames.get(user.name), user.name, { truncate: 13 })}</div>
          })}
        </section>
        {games && games.map((game) => {
          const gameId = `${game.awayTeam?.name || 'away'}-${game.homeTeam?.name || 'home'}`;
          return (
            <section style={styles.gameCard} key={gameId}>
              <div style={styles.header}>
                <div>
                  {game.awayTeam.logo && (
                    <img style={styles.icon} src={getLogoUrl(game.awayTeam.logo)} alt="away logo" />
                  )}
                </div>
                <div style={styles.cell}>vs</div>
                <div>
                  {game.homeTeam.logo && (
                    <img style={styles.icon} src={getLogoUrl(game.homeTeam.logo)} alt="home logo" />
                  )}
                </div>
              </div>
              {/* Show final score under logos if game is complete */}
              {game.disabled && game.homeScore !== undefined && game.awayScore !== undefined && (
                <div style={{
                  textAlign: 'center',
                  fontSize: '0.75em',
                  color: '#666',
                  marginTop: '-8px',
                  marginBottom: '4px',
                  fontWeight: '600',
                }}>
                  {game.awayScore} - {game.homeScore}
                </div>
              )}
              {allPicksForWeek && allPicksForWeek.map((user) => {
                const pick = picksByUser[user.name]?.[game.id];
                if (pick) {
                  // Derive winner from game scores — works for all past games without needing to run scoring
                const winningTeam = (game.homeScore !== undefined && game.awayScore !== undefined)
                  ? (Number(game.homeScore) > Number(game.awayScore) ? game.homeTeam.name : game.awayTeam.name)
                  : (winningTeams && winningTeams[gameId]);
                  const pickedWinner = pick.homeWin ? game.homeTeam.name : game.awayTeam.name;
                  const isWinner = winningTeam === pickedWinner;
                  const showPicks = game.disabled || isAdmin;

                  if (!showPicks) {
                    return (
                      <div style={styles.row} key={user.name}>
                        <div style={styles.score}>Locked</div>
                      </div>
                    )
                  }

                  return (
                    <div key={user.name}>
                      <div style={styles.row}>
                      {game.isPickScore ?
                        pick.scoreDifference !== undefined ? (
                          <Tooltip title={pick.isExactMatch ? "Exact Score!" : `${pick.scoreDifference} points away`} enterDelay={500}>
                            <div style={{...styles.score, ...(pick.isWinningPick ? styles.winningPick : {})}}>
                              <div>{pick.awayScore} - {pick.homeScore}</div>
                            </div>
                          </Tooltip>
                        ) : (
                          <div style={styles.score}>
                            <div>{pick.awayScore} - {pick.homeScore}</div>
                          </div>
                        )
                      :
                        winningTeam ? (
                          // Game finished — show ✓ or ✗ instead of checkboxes
                          <div style={{
                            ...styles.score,
                            fontSize: '1.2em',
                            fontWeight: '700',
                            color: isWinner ? '#2e7d32' : '#c62828',
                          }}>
                            {isWinner ? '✓' : '✗'}
                          </div>
                        ) : (
                          // Game not finished — show checkboxes
                          <div style={styles.score}>
                            <input className="check" type='checkbox' checked={pick.awayWin} disabled={true} />
                            <input className="check" type='checkbox' checked={pick.homeWin} disabled={true} />
                          </div>
                        )
                      }
                      </div>
                    </div>
                  )
                } else {
                  return <div style={styles.row} key={user.name}></div>;
                }
              })}
            </section>
          )
        })}
      </section>
    );

    return (
        <Box style={{
            ...styles.container,
            ...(isMobile && { marginLeft: '8px', marginRight: '8px', marginTop: '80px' }),
        }}>
            <section style={{
                ...styles.leaderboard,
                ...(isMobile && { maxWidth: '100%', padding: '10px 0' }),
            }}>
              <h3>{season} Standings</h3>
              {
                isLoading && <Box sx={{ width: '100%' }}>
                  <LinearProgress />
                </Box>
              }
              {fetchError && (
                <Box sx={{ color: 'error.main', textAlign: 'center', mt: 2 }}>
                  {fetchError}
                </Box>
              )}
              <LeaderboardTable season={season}/>
            </section>

            <section style={{
                ...styles.picks,
                ...(isMobile && { padding: '10px 0', width: '100%' }),
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                <span>Week:</span>
                {isMobile ? (
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select value={page} onChange={(e) => turnPage(e, e.target.value)}>
                      {Array.from({ length: numberOfWeeks }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Pagination count={numberOfWeeks} shape="rounded" page={page} onChange={turnPage}/>
                )}
              </div>
              {isMobile ? renderMobilePicks() : renderDesktopPicks()}
            </section>
        </Box>
    );
}

export default Leaderboard;
